import React, { useState, useEffect, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import { transformPaddingToTailwind, computeLightOrDark } from '../../helpers/style'
import { MarkupContext } from '../../providers/Markup'
import { JumpMenu, JumpMenuDevice } from '../helpers/article/JumpMenu'
import { dataLayerPush } from '../../helpers/thirdparty'
import { getFriendlyName } from '../../helpers/markup'
import { smoothScrollToId } from '../../helpers/smoothScroll'
import { getHashTargetId } from '../../helpers/hashScroll'
// Import all available components for dynamic rendering
import Accordion from './Accordion'
import BasicContent from './BasicContent'
import BasicContentWithColumns from './BasicContentWithColumns'
import CalloutCard from './CalloutCard'
import CardListCurated from './CardListCurated'
import CardListDynamic from './CardListDynamic'
import ContentFiftybyFifty from './ContentFiftybyFifty'
import HeroBanner from './HeroBanner'
import HeroCard from './HeroCard'
import InformationBanner from './InformationBanner'
import InformationBox from './InformationBox'
import SearchResult from './SearchResult'
import SubPageBanner from './SubPageBanner'
import ThirdParty from './ThirdParty'
import ProductFiftyFiftyContent from './ProductFiftyFiftyContent'
import TagListing from './TagListing'
import TestimonialCarousel from './TestimonialCarousel'
import StepsCards from './StepsCards'
import JumpToSection from './JumpToSection'
import EventKeyDetails from './EventKeyDetails'

// Layout variants for the article container, driven by the `variant` prop
// supplied from the parent block JSON. Anything other than the multi-section
// value falls back to the original single-page anchor behaviour.
const ARTICLE_VARIANT = {
  singlePageAnchor: 'Single page anchor',
  multiSectionAnchor: 'Multi section anchor',
}

/**
 * A deep link (`/article#section-id`) has to open on the section it names rather than
 * the first one. In the multi-section variant only the active section is mounted, so
 * without this the fragment target never enters the DOM at all and hashScroll has
 * nothing to find. Falls back to the first section for ordinary loads.
 */
const getInitialSectionId = (items) => {
  const hashId = getHashTargetId()
  const match = items.find((item) => item.id === hashId)
  return match ? match.id : items[0].id
}

const ArticleContainer = (props) => {
  const { getPaletteColor } = useContext(MarkupContext)
  const [activeSection, setActiveSection] = useState('')
  const [navigationItems, setNavigationItems] = useState([])
  const navigationItemsRef = useRef([])
  const [useSidebar, setUseSidebar] = useState(true)

  // Multi-section anchor only renders the component tied to the active nav item.
  // Matched leniently (case/whitespace) but defaults to single-page behaviour.
  const isMultiSection =
    (props.variant || '').trim().toLowerCase() === ARTICLE_VARIANT.multiSectionAnchor.toLowerCase()

  // Component mapping for dynamic rendering
  const components = {
    accordion: Accordion,
    basicContent: BasicContent,
    basicContentWithColumns: BasicContentWithColumns,
    calloutCard: CalloutCard,
    cardListCurated: CardListCurated,
    cardListDynamic: CardListDynamic,
    contentFiftybyFifty: ContentFiftybyFifty,
    heroBanner: HeroBanner,
    heroCard: HeroCard,
    informationBanner: InformationBanner,
    informationBox: InformationBox,
    searchResult: SearchResult,
    subPageBanner: SubPageBanner,
    thirdParty: ThirdParty,
    productFiftyFiftyContent: ProductFiftyFiftyContent,
    tagListing: TagListing,
    testimonials: TestimonialCarousel,
    stepsCards: StepsCards,
    jumpToSection: JumpToSection,
    eventKeyDetails: EventKeyDetails,
  }

  useEffect(() => {
    setUseSidebar(props.sidebarOnOff)
  }, [])

  const sidebarTitle = props.sidebarTitle || 'Jump to a section'
  const [isSmall, setIsSmall] = useState(false)
  const [isMedium, setIsMedium] = useState(false)
  const [isLarge, setIsLarge] = useState(false)
  const resizeTimeoutRef = useRef(null)
  const isResizingRef = useRef(false)
  const resizeCooldownRef = useRef(null)

  const updateBreakpoints = () => {
    setIsSmall(window.matchMedia('(max-width: 767px)').matches)
    setIsMedium(window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches)
    setIsLarge(window.matchMedia('(min-width: 1024px)').matches)

    const navItems = navigationItemsRef.current
    if (navItems.length > 0) {
      const firstTrigger = document.getElementById(`${navItems[0].id}-trigger`)
      if (firstTrigger) {
        let parent = firstTrigger.closest('.overflow-y-auto')
        if (parent) {
          sidebarContainerRef.current = parent
        }
      }
    }

    if (resizeCooldownRef.current) {
      clearTimeout(resizeCooldownRef.current)
    }
    resizeCooldownRef.current = setTimeout(() => {
      isResizingRef.current = false
    }, 300)
  }

  const handleResize = () => {
    isResizingRef.current = true

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
      scrollTimeoutRef.current = null
    }

    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current)
    }

    resizeTimeoutRef.current = setTimeout(() => {
      updateBreakpoints()
    }, 100)
  }

  useEffect(() => {
    updateBreakpoints()
    window.addEventListener('resize', handleResize, { passive: true })
    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
      if (resizeCooldownRef.current) {
        clearTimeout(resizeCooldownRef.current)
      }
    }
  }, [navigationItems])

  useEffect(() => {
    if (props.articleComponents && props.articleComponents.length > 0) {
      const items = props.articleComponents
        .filter((component) => component.jumpToId)
        .filter((component) => component.sidebarMenuTitle)
        .map((component) => ({
          id: component.jumpToId,
          label: getFriendlyName(component.sidebarMenuTitle),
          componentName: component.componentName,
        }))
      setNavigationItems(items)
      navigationItemsRef.current = items

      if (items.length > 0) {
        setActiveSection(getInitialSectionId(items))
      }
    }
  }, [props.articleComponents])

  const handleNavigationClick = (event, sectionId) => {
    if (isMultiSection) {
      // Swap the visible component, then bring the freshly rendered section into
      // view once React has committed the change (mirrors the setTimeout(0)
      // pattern used elsewhere in this component for post-render DOM work).
      setActiveSection(sectionId)
      setTimeout(() => smoothScrollToId(sectionId), 0)
    } else {
      smoothScrollToId(sectionId)
    }

    let target = event.target
    if (!target) return

    if (target.nodeName !== 'BUTTON') {
      target = target.closest('button')
    }

    dataLayerPush({
      event: 'navigation_in_page_click',
      click_text: target.innerText,
    })
  }

  const scrollTimeoutRef = useRef(null)
  const lastScrollYRef = useRef(0)
  const sidebarContainerRef = useRef(null)
  const initTimeoutRef = useRef(null)

  useEffect(() => {
    // Multi-section renders a single component at a time, so there is nothing to
    // scroll-spy — the active section is driven purely by navigation clicks.
    if (isMultiSection) return

    const findSidebarContainer = () => {
      if (navigationItems.length > 0) {
        const firstTrigger = document.getElementById(`${navigationItems[0].id}-trigger`)
        if (firstTrigger) {
          let parent = firstTrigger.closest('.overflow-y-auto')
          if (parent) {
            sidebarContainerRef.current = parent
          }
        }
      }
    }

    initTimeoutRef.current = setTimeout(findSidebarContainer, 0)

    const isElementVisible = (element, container) => {
      if (!element || !container) return false
      const elementRect = element.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom
    }

    const calculateScrollPosition = (triggerRect, containerRect, container, isScrollingDown) => {
      const triggerTop = triggerRect.top - containerRect.top + container.scrollTop
      const triggerBottom = triggerTop + triggerRect.height
      const containerHeight = container.clientHeight
      if (isScrollingDown) {
        return triggerBottom - containerHeight
      }
      return triggerTop
    }

    const scrollSidebarToTrigger = (trigger, container, isScrollingDown) => {
      if (isElementVisible(trigger, container)) {
        return
      }
      const triggerRect = trigger.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const scrollPosition = calculateScrollPosition(triggerRect, containerRect, container, isScrollingDown)
      container.scrollTo({ top: scrollPosition, behavior: 'smooth' })
    }

    const updateSidebarScroll = (newActiveSection, isScrollingDown) => {
      const isLargeScreen = window.matchMedia('(min-width: 1024px)').matches
      if (!isLargeScreen || isResizingRef.current) {
        return
      }
      const trigger = document.getElementById(`${newActiveSection}-trigger`)
      const container = sidebarContainerRef.current
      if (!trigger || !container) {
        return
      }
      scrollSidebarToTrigger(trigger, container, isScrollingDown)
    }

    const findActiveSectionIndex = (sections) => {
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        const rect = section.getBoundingClientRect()
        if (rect.top <= 120) {
          return i
        }
      }
      return -1
    }

    const updateActiveSection = (evt) => {
      if (navigationItems.length === 0) {
        return
      }
      if (evt && !evt.srcElement) {
        return
      }
      const sections = navigationItems.map((item) => document.getElementById(item.id)).filter(Boolean)
      const currentScrollY = window.scrollY ?? document.documentElement.scrollTop ?? 0
      const isScrollingDown = currentScrollY > lastScrollYRef.current
      lastScrollYRef.current = currentScrollY
      const activeIndex = findActiveSectionIndex(sections)
      if (activeIndex === -1) {
        return
      }
      const newActiveSection = navigationItems[activeIndex].id
      setActiveSection(newActiveSection)
      updateSidebarScroll(newActiveSection, isScrollingDown)
    }

    const handleScroll = (evt) => {
      if (navigationItems.length === 0) return
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      scrollTimeoutRef.current = setTimeout(() => {
        updateActiveSection(evt)
      }, 50)
    }

    lastScrollYRef.current = window.scrollY ?? document.documentElement.scrollTop ?? 0

    window.addEventListener('scroll', handleScroll, { passive: true })
    if (navigationItems.length > 0) {
      const sections = navigationItems.map((item) => document.getElementById(item.id)).filter(Boolean)
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        const rect = section.getBoundingClientRect()
        if (rect.top <= 120) {
          setActiveSection(navigationItems[i].id)
          break
        }
      }
    }

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current)
      }
    }
  }, [navigationItems, isMultiSection])

  const [backgroundColor, setBackgroundColor] = useState('transparent')
  const [lightOrDarkValue, setLightOrDarkValue] = useState('light')

  useEffect(() => {
    setLightOrDarkValue(computeLightOrDark(backgroundColor, getPaletteColor))
  }, [backgroundColor, getPaletteColor])

  useEffect(() => {
    const bgColor = backgroundColor.replace('#', '')
    setBackgroundColor((prevState) => (bgColor ? `#${bgColor}` : prevState))
    setLightOrDarkValue(computeLightOrDark(bgColor, getPaletteColor))
    // Intentionally mount-only (legacy): reads initial backgroundColour state only.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [])

  const renderComponent = (component) => {
    const { componentName, componentData, jumpToId } = component
    const ComponentToRender = components[componentName]

    if (ComponentToRender) {
      delete componentData.key
    } else {
      console.warn(`Component type ${componentName} was not found in ArticleContainer. Ignoring...`)
    }

    return (
      <div id={jumpToId} className="scroll-mt-24">
        {ComponentToRender ? (
          <ComponentToRender {...componentData} fromArticleContainer={true} />
        ) : (
          <div className="prose max-w-none">
            <h2 className="text-2xl font-bold mb-4">Component: {componentName}</h2>
            <pre className="p-4 rounded text-sm overflow-auto">{JSON.stringify(componentData, null, 2)}</pre>
          </div>
        )}
      </div>
    )
  }

  const componentPadding = transformPaddingToTailwind(props.componentPadding, false, props.breadcrumbs)

  const allComponents = props.articleComponents || []
  // Both variants render *every* child into the DOM so mount-time integrations
  // (notably embedded D365 forms, which FormLoader.bundle.js only initialises
  // for the [data-form-id] nodes present when it loads) come up once and survive
  // navigation. Single-page anchor shows them all and scroll-spies between them;
  // multi-section anchor ("one section per anchor") keeps only the active section
  // visible and hides the rest via CSS rather than unmounting them.
  const firstNavigableId = allComponents.find(
    (component) => component.jumpToId && component.sidebarMenuTitle,
  )?.jumpToId
  const effectiveActiveSection = activeSection || firstNavigableId

  // The API flattens each Article Block's components into sibling entries and puts the block's
  // menu label on the FIRST one only (ComponentFactory.ProcessArticleBlock), so a component
  // belongs to the most recent labelled entry at or before it. Comparing each component's own
  // jumpToId instead left every non-first component in a block permanently hidden — no nav item
  // ever points at its id, so it could not be reached by clicking either (NDS-363). Components
  // before the first label fall into the first navigable section via the seed.
  const sectionIdByIndex = []
  let currentSectionId = firstNavigableId
  allComponents.forEach((component, index) => {
    if (component.jumpToId && component.sidebarMenuTitle) {
      currentSectionId = component.jumpToId
    }
    sectionIdByIndex[index] = currentSectionId
  })

  const isChildVisible = (component, index) =>
    !isMultiSection || sectionIdByIndex[index] === effectiveActiveSection

  return (
    <section
      data-type="articleContainer"
      data-index={props.idx}
      style={{
        '--mobile-bg-url': props.mobileBackgroundImage ? `url(${props.mobileBackgroundImage.src})` : null,
        '--desktop-bg-url': props.desktopBackgroundImage ? `url(${props.desktopBackgroundImage.src})` : null,
        '--bg-color': backgroundColor ?? 'transparent',
      }}
      className={componentPadding}
    >
      <div className="container mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-20 items-start">
          {useSidebar && navigationItems.length > 0 && (
            <div className="col-span-full lg:col-span-1 lg:sticky lg:top-24">
              {(isSmall || isMedium) && (
                <JumpMenuDevice
                  navigationItems={navigationItems}
                  sidebarTitle={sidebarTitle}
                  activeSection={activeSection}
                  handleNavigationClick={handleNavigationClick}
                />
              )}
              {isLarge && (
                <JumpMenu
                  navigationItems={navigationItems}
                  sidebarTitle={sidebarTitle}
                  activeSection={activeSection}
                  handleNavigationClick={handleNavigationClick}
                />
              )}
            </div>
          )}

          {/* Main Content */}
          <div
            className={`col-span-full ${navigationItems.length > 0 && useSidebar ? 'lg:col-span-3' : 'lg:col-span-4'}`}
            data-jump-scan-root
          >
            <div className="space-y-12">
              {allComponents.length > 0 ? (
                allComponents.map((component, index) => (
                  <div
                    key={`article-container-component-${component.jumpToId || index}`}
                    className={isChildVisible(component, index) ? undefined : 'hidden'}
                  >
                    {renderComponent(component)}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-grey">No article components found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

ArticleContainer.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  sidebarTitle: PropTypes.string,
  sidebarOnOff: PropTypes.bool,
  variant: PropTypes.string,
  componentPadding: PropTypes.string,
  breadcrumbs: PropTypes.bool,
  mobileBackgroundImage: PropTypes.object,
  desktopBackgroundImage: PropTypes.object,
  backgroundColor: PropTypes.string,
  idx: PropTypes.number,
  articleComponents: PropTypes.arrayOf(
    PropTypes.shape({
      componentName: PropTypes.string.isRequired,
      componentData: PropTypes.object,
      jumpToId: PropTypes.string,
      sidebarMenuTitle: PropTypes.string,
    }),
  ),
}

export default ArticleContainer
